import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location } from 'react-router-dom'
import {
  NavigateFunction,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { addTrackToGroups } from '@disco/state-management/actions/ExplorerActions'
import {
  getGroup,
  getGroupsWithNesting,
} from '@disco/state-management/selectors/groups'
import { getRoutingPreviousLocation } from '@disco/state-management/selectors/routing'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { sendTrackDownloadEventThrottled } from '@disco/utils/eventsApi'
import { getModalBestReturnTo } from '@disco/utils/modals'

import type { Group } from 'types/group'
import type { State } from 'types/redux'

import AddToChannelModal from 'components/common/custom-libraries/add-to-channel'
import DocumentHeadTags from 'components/document-head-tags'

import type { RootState } from 'store/configureStore'

interface OwnProps {
  params: {
    trackId: string
  }
  navigate: NavigateFunction
  previousLocation: Location
  location: Location
  searchParams: URLSearchParams
}

function mapStateToProps(state: RootState, props: OwnProps) {
  return {
    track: getTrack(state, {
      id: parseInt(props.params.trackId, 10),
    }),
    sourceGroup: getGroup(state, {
      groupId: parseInt(props.searchParams.get('source_group'), 10),
    }),
    sourcePlaylistId: props.searchParams.get('source_playlist')
      ? parseInt(props.searchParams.get('source_playlist'), 10)
      : undefined,
    groups: getGroupsWithNesting(state),
    previousLocation: getRoutingPreviousLocation(state),
  }
}

const mapDispatchToProps = {
  addTrackToGroups,
  sendMixpanelEvent,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
type Props = PropsFromRedux & OwnProps

class AddToChannel extends Component<Props> {
  _onClose = () => {
    this.props.navigate(
      getModalBestReturnTo(this.props.location, this.props.previousLocation),
    )
  }
  _onAddToGroup = groupsToAdd => {
    const existingGroups = groupsToAdd
      .filter(groupToAdd => !groupToAdd.created)
      .map(groupToAdd => {
        return this.props.groups.find(group => group.id === groupToAdd.value)
      })

    if (existingGroups.length) {
      this.props.addTrackToGroups(this.props.track, existingGroups)
      this.props.sendMixpanelEvent('Add to channel from track menu')
    }

    if (
      this.props.sourceGroup &&
      INBOX_TYPES.includes(this.props.sourceGroup.group_type)
    ) {
      sendTrackDownloadEventThrottled(this.props.track.id, 'in-app', {
        group_id: this.props.sourceGroup.id,
        playlist_id: this.props.sourcePlaylistId,
      })
    }

    this._onClose()
  }

  render() {
    return (
      <div>
        <DocumentHeadTags title="Add to channel | DISCO" />
        <AddToChannelModal
          groups={this.props.groups}
          source={this.props.track}
          onSubmit={this._onAddToGroup}
          onClose={this._onClose}
        />
      </div>
    )
  }
}

const ConnectedAddToChannel = connector(AddToChannel)

// Wrapper component to provide router params and navigation to the connected component
const AddToChannelWithRouter = (props: any) => {
  const params = useParams<{ trackId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  return (
    <ConnectedAddToChannel
      {...props}
      params={params}
      navigate={navigate}
      searchParams={searchParams}
    />
  )
}

export default AddToChannelWithRouter
